import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { cx } from 'passiv-libs'
import { Loading, AppWrapper, Img, Input, Modal, Text, Tick } from 'passiv-components'

const Login = ({
  refs,
  disabled,
  trialSettings,
  loading,
  error,
  username,
  serverUrl,
  onClickQuery,
  onClickForgot,
  onClickPrivacy,
  onClickReset,
  onClearError,
  onLogin,
  autoLogin,
  onToggleAutoLogin,
  onSelectTrialSettings
}) => {
  const [showAnimation, setShowAnimation] = useState(true)

  useEffect(() => {
    const ANIMATION = {
      3000: () => {
        setShowAnimation(false)
      }
    }
    Object.keys(ANIMATION).forEach(x => {
      setTimeout(ANIMATION[x], x)
    })
  }, [])

  const errorMessage =
    error === 'INTERNET' || error === 'SERVER'
      ? 'Your device is having trouble connecting to our servers. Please ensure your device is connected to the internet to continue using the app.'
      : 'The username or password you entered is incorrect.\nYou will be locked out for 20 minutes after three attempts.'

  const modalButtons =
    error === 'INTERNET'
      ? [
          {
            name: 'ok',
            text: 'Ok',
            onClick: onClearError
          }
        ]
      : [
          {
            name: 'ok',
            text: 'Ok',
            onClick: onClearError
          },
          {
            name: 'reset',
            text: 'Reset password',
            onClick: onClickReset
          }
        ]

  return (
    <>
      {loading && <Loading.Default />}
      {showAnimation && <div className="flex-center h-full w-full absolute bg-in-red-grad left-0 top-0">
        <Img 
          className={'w-25 transition duration-500'} 
          name="logo.png" 
          ariaLabel="PassivLiving Logo"
        />
        <div className={'w-full h-full absolute left-0 top-0 transition duration-500'}>
          <Loading.Login />
        </div>
      </div>}

      {!showAnimation && <div className={'h-full relative z-10 transition duration-500'}>
        <AppWrapper
          dataContainer="login"
          titleHeader="Login"
          titleContainer="Welcome to PassivLiving"
          disabled={disabled}
          // classes={{ container: 'h-initial' }}
          renderPortal={
            error
              ? () => (
                  <Modal
                    id="error"
                    open={true}
                    type="alert"
                    title={(error === 'INTERNET' || error === 'SERVER') ? "Something's wrong" : "Error"}
                    text={errorMessage}
                    buttons={modalButtons}
                  />
                )
              : null
          }
          buttons={[
            {
              name: 'login',
              text: 'Login',
              onClick: onLogin
            }
          ]}
          onClickQuery={onClickQuery}
        >
          <Input
            className="border-b border-border-dark mb-1"
            ref={e => {
              refs.username(e)
              refs.username.current = e
            }}
            type="email"
            name="username"
            id="login-username"
            label="Username"
            defaultValue={autoLogin ? username : ''}
            onChange={() => { if (refs.username.current.value !== username) refs.password.current.value = ''}}
          />
          <Input
            className="border-b border-border-dark mb-1"
            ref={e => {
              refs.password(e)
              refs.password.current = e
            }}
            type="password"
            name="password"
            id="login-password"
            label="Password"
          />

          <div className={!trialSettings ? 'hidden' : ''}>
            <Input
              className="border-b border-border-dark mb-1"
              ref={e => {
                refs.server(e)
                refs.server.current = e
              }}
              type="text"
              name="server"
              id="login-server"
              label="Server"
              defaultValue={serverUrl}
            />

            <div className="rounded-lg p-2 m-2 border border-bg-grey text-center">
              <Text type="label" className="">
                Refresh the page after changing the server URL below
              </Text>
              <Text
                type="label"
                className="mt-2 rounded-lg text-center p-1 bg-blue text-white cursor-pointer"
                onClick={() => window.location.reload()}
              >
                Refresh
              </Text>
            </div>
          </div>

          <div className="px-2 pb-1">
            {trialSettings && (
              <div className="shadow-md cursor-pointer p-1 mt-3">
                <select
                  onChange={onSelectTrialSettings}
                  className="appearance-none text-black rounded-0 border-none text-base w-full"
                >
                  <option value={window.location.origin}>Self</option>
                  <option value="https://passivliving.com">Prod</option>
                  <option value="https://dmz2.passivonline.com">DMZ2</option>
                  <option value="https://dmz3.passivonline.com">DMZ3</option>
                  <option value="https://dmz7.passivonline.com">DMZ7</option>
                </select>
              </div>
            )}

            <Text
              type="label"
              className="text-blue mt-3 mb-2 cursor-pointer"
              onClick={onClickForgot}
            >
              Forgotten password?
            </Text>

            <div
              className="flex items-center justify-between py-1 mb-1 select-none cursor-pointer"
              onClick={onToggleAutoLogin}
            >
              <Text>Keep me signed in</Text>
              <Tick active={autoLogin} />
            </div>

            <Text type="caption" className="text-grey -mt-1 mb-4">
              Do not tick this if you are accessing from a shared device
            </Text>

            <Text type="label" className="text-grey my-2">
              This site uses cookies and by using this site you are consenting to this.{' '}
              <span className="cursor-pointer text-blue" onClick={onClickPrivacy}>
                Find out why we use cookies.
              </span>
            </Text>
          </div>
        </AppWrapper>
      </div>}
    </>
  )
}

Login.propTypes = {
  refs: PropTypes.object,
  disabled: PropTypes.bool,
  trialSettings: PropTypes.bool,
  seenAnimation: PropTypes.bool,
  loading: PropTypes.bool,
  error: PropTypes.oneOf(['', 'INTERNET', 'SERVER']),
  username: PropTypes.string,
  serverUrl: PropTypes.string,
  onClickQuery: PropTypes.func,
  onClickForgot: PropTypes.func,
  onClickPrivacy: PropTypes.func,
  onClickReset: PropTypes.func,
  onClearError: PropTypes.func,
  onLogin: PropTypes.func,
  autoLogin: PropTypes.bool,
  onToggleAutoLogin: PropTypes.func,
  onSelectTrialSettings: PropTypes.func
}

export default Login
